import { useState, ChangeEvent, useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { IconFile } from 'UIPackage/IconsModule/components/IconFile';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { INVALID_TYPE_FILE } from 'CorePackage/MainModule/constants/notices';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';

import styles from './KnmSignDocumentModal.module.scss';
import { knmDocumentsUpload } from '../actions/knmDocumentsActions';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getMaxFileSize } from '../../../CorePackage/MainModule/common/getMaxFileSize';
import { addNoticeKnmFileSizeErrorDetailed } from '../../../CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getKnmSystemType } from '../../MainModule/selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../../MainModule/enums/KnmSystemTypesEnum';

interface IKnmSignDocumentModalProps {
  acceptedFiles?: string;
  isVisibleModal: boolean;
  isWatchOnly?: boolean;
  fieldName: string;
  onCloseSignModal: () => void;
  onSaveSignedDocument: (document: File, signatureInfo: IKnmAttachment['signatureInfo']) => void;
}

interface IKnmSignDocumentModalStoreProps {
  knmSystemType: KnmSystemTypesEnum | undefined;
  settings: IAdminSetting[];
}

const KnmSignDocumentModalWithoutStore = ({
  acceptedFiles,
  isVisibleModal,
  isWatchOnly,
  fieldName,
  onCloseSignModal,
  onSaveSignedDocument,
  knmSystemType,
  settings,
}: IKnmSignDocumentModalProps & IKnmSignDocumentModalStoreProps) => {
  const [document, setDocument] = useState<File | null>(null);
  const [signs, setSigns] = useState<Array<File>>([]);
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const isDisabledButton = !document;

  useEffect(() => {
    if (!isVisibleModal) {
      setDocument(null);
      setSigns([]);
    }
  }, [isVisibleModal]);

  const renderAttachment = (attachment: File, index?: number) => (
    <div className={styles.AttachedDocument} key={index || attachment.name}>
      <IconFile fileName={attachment.name} />
      <span className={styles.AttachedDocumentName}>{attachment.name}</span>
      {!isWatchOnly && (
        <button
          className={styles.RemoveButton}
          type="button"
          onClick={index !== undefined ? handleRemoveSign(index) : handleRemoveDocument}
        >
          x
        </button>
      )}
    </div>
  );

  const handleUploadButton = useCallback(() => {
    dispatch(knmDocumentsUpload(document, signs, onSaveSignedDocument));
  }, [dispatch, document, onSaveSignedDocument, signs]);

  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));
  }, [dispatch, maxFileSize]);

  const handleInputAttachment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];
        const fileName = file.name.toLocaleLowerCase();
        const acceptedTypes = e.target.accept.split(',');

        if (acceptedTypes.find(item => fileName.endsWith(item))) {
          const isSizeValid = file.size <= maxFileSize * MB;
          const isSign = fileName.split('.').pop() === 'sig';

          if (!isSizeValid) {
            handleFileSizeError();
          } else {
            isSign ? setSigns(prev => [...prev, file]) : setDocument(file);
          }
        } else {
          dispatch(
            addNoticeAction({
              renderContent: INVALID_TYPE_FILE,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT_TYPE,
            }),
          );
        }
      }
      e.target.value = '';
    },
    [dispatch, handleFileSizeError],
  );

  const handleAfterCloseModal = useCallback(() => {
    setDocument(null);
    setSigns([]);
  }, []);

  const handleRemoveDocument = useCallback(() => {
    setDocument(null);
  }, []);

  const handleRemoveSign = useCallback(
    (index: number) => () => {
      setSigns(prev => prev.filter((el: File, idx: number) => idx !== index));
    },
    [],
  );

  const documentInputName = `${fieldName}UploadDocument`;
  const signInputName = `${fieldName}UploadSign`;

  return (
    <Modal visible={isVisibleModal} onClose={onCloseSignModal} afterClose={handleAfterCloseModal}>
      <div className={styles.SignModalContainer}>
        <ModalHeader title="Загрузка документов" weight="bold" placement="centre" sizeText="medium" color="black" />
        <div className={styles.Attachments}>
          <div className={styles.AttachmentsContainer}>
            <div className={styles.AttachmentHeader}>
              <span>Документ</span>
              <label className={styles.AddAttachmentLabel} htmlFor={documentInputName}>
                Добавить
              </label>
              <input
                id={documentInputName}
                name={documentInputName}
                type="file"
                accept={acceptedFiles || '.ods,.xls,.xlsx,.doc,.docx,.pdf'}
                style={{ display: 'none' }}
                onChange={handleInputAttachment}
              />
            </div>
            {document && renderAttachment(document)}
          </div>
          <div className={styles.AttachmentsContainer}>
            <div className={styles.AttachmentHeader}>
              <span>Подписи</span>
              <label className={styles.AddAttachmentLabel} htmlFor={signInputName}>
                Добавить
              </label>
              <input
                id={signInputName}
                name={signInputName}
                type="file"
                accept=".sig"
                style={{ display: 'none' }}
                onChange={handleInputAttachment}
              />
            </div>
            {signs.length ? signs.map((sign, idx) => renderAttachment(sign, idx)) : null}
          </div>
        </div>
        <Button
          className={styles.UploadButton}
          text="Загрузить"
          id="uploadButton"
          color="primary"
          onClick={handleUploadButton}
          disabled={isDisabledButton}
        />
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IKnmSignDocumentModalStoreProps => ({
  knmSystemType: getKnmSystemType(state),
  settings: getAdminFetchSettings(state),
});

export const KnmSignDocumentModal = connect(mapStateToProps)(KnmSignDocumentModalWithoutStore);
